import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Link,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import CameraOutlinedIcon from "@mui/icons-material/CameraOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import type { SpecificationItemDto } from "generated/models";
import { BUNDLE_PRODUCT_CLASS } from "../../../types/ecommerce/constants";
import { telemetryProps } from "../../../instrumentation/gtm";
import { ProductImageGallery } from "./ProductImageGallery";
import { ProductSpecs, type SpecFallbackRow } from "./ProductSpecs";
import { ProductStockInventoryTab } from "./ProductStockInventoryTab";
import { QuantitySelector } from "./QuantitySelector";
import { StockDot, srOnlySx } from "./StockDot";
import {
  STOCK_STATUS_CONFIG,
  getAccessiblePriceLabel,
  getVisiblePriceLabel,
  mapInventoryStatusToStockStatus,
  type PriceFormatter,
} from "./productDrawerFormatting";
import {
  fullSpecificationToSections,
  isCustomerVisibleCatalogText,
  normalizeCatalogText,
} from "./specSections";
import {
  ProductDrawerTab,
  type DrawerImage,
  type ProductDrawerCartConfig,
  type ProductDrawerSpecification,
  type ProductDrawerStockTabConfig,
  type ProductDrawerViewModel,
} from "./types";

export interface ProductDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Null renders the unavailable/error state. */
  product: ProductDrawerViewModel | null;
  images?: DrawerImage[];
  /** Grouped specification feed. When present it renders as searchable spec sections. */
  fullSpecification?: SpecificationItemDto[] | null;
  /** Simple dimensions/weight fallback used when there are no grouped spec sections. */
  specificationFallback?: ProductDrawerSpecification | null;
  /** While true, shows a spinner in the product-details panel. */
  loading?: boolean;
  /** Optional "View Full Details" external link; omit to hide it. */
  productDetailUrl?: string | null;
  /** Override config-system detection (defaults to productClass === bundle). */
  isConfigSystem?: boolean;
  /** Header eyebrow label. Defaults to "Quick View". */
  headerLabel?: string;
  /** Telemetry event section/name prefix (e.g. "standards_quick_view"). */
  telemetrySection?: string;
  /** Add-to-cart capability. Omit for read-only consumers. */
  cart?: ProductDrawerCartConfig;
  /** Stock / Inventory tab capability. Omit to render a single panel with no tabs. */
  stockTab?: ProductDrawerStockTabConfig;
  /** Initial tab when the stock tab is enabled. */
  initialTab?: ProductDrawerTab;
  /** Scroll the product-details panel to the specs section once the drawer has loaded. */
  scrollToSpecsOnOpen?: boolean;
  /** Formats the price amount (e.g. in the storefront currency). Defaults to USD. */
  formatPrice?: PriceFormatter;
  /** Notified whenever the active tab changes (e.g. to lazily enable availability fetching). */
  onTabChange?: (tab: ProductDrawerTab) => void;
  /** Raise above a host Dialog (MUI modal defaults to 1300) when opened from within one. */
  zIndex?: number;
  /** Render the drawer content inside a custom shell (e.g. a stacked drawer) instead of an
   * anchored MUI Drawer. Receives the fully rendered drawer body. */
  renderShell?: (content: ReactNode) => ReactNode;
}

const telemetryNameSectionBySection: Record<string, string> = {
  it_projects_quick_view: "it_projects",
  orders_stacked_quick_view: "orders_stacked",
  standards_mgmt_quick_view: "standards_mgmt",
};

export function ProductDetailDrawer({
  open,
  onClose,
  product,
  images = [],
  fullSpecification,
  specificationFallback,
  loading = false,
  productDetailUrl,
  isConfigSystem: isConfigSystemProp,
  headerLabel = "Quick View",
  telemetrySection = "product_quick_view",
  cart,
  stockTab,
  initialTab = ProductDrawerTab.ProductDetails,
  scrollToSpecsOnOpen = false,
  formatPrice,
  onTabChange,
  zIndex,
  renderShell,
}: ProductDetailDrawerProps) {
  const hasStockTab = !!stockTab;
  const hasCart = !!cart;

  const [tab, setTab] = useState<ProductDrawerTab>(initialTab);
  const [quantity, setQuantity] = useState(1);
  const [copiedPartNumber, setCopiedPartNumber] = useState<
    "mfr" | "shi" | null
  >(null);
  const copyResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const specsRef = useRef<HTMLDivElement | null>(null);
  const detailsScrollRef = useRef<HTMLDivElement | null>(null);

  const productKey = product?.productDescriptor ?? product?.productName ?? null;

  // Layout effects so the correct tab paints on the first frame when the drawer opens.
  // `open` is a dependency so reopening the same product starts on `initialTab` again.
  useLayoutEffect(() => {
    if (!open) return;
    setTab(hasStockTab ? initialTab : ProductDrawerTab.ProductDetails);
  }, [open, productKey, initialTab, hasStockTab]);

  // Only a new product resets these; a routed tab switch changes `initialTab` and must keep them.
  useLayoutEffect(() => {
    setQuantity(1);
    setCopiedPartNumber(null);
  }, [productKey]);

  useEffect(
    () => () => {
      if (copyResetRef.current) clearTimeout(copyResetRef.current);
    },
    [],
  );

  const isProductDetailsVisible =
    !hasStockTab || tab === ProductDrawerTab.ProductDetails;

  // Deferred a frame so the panel has laid out before scrolling. Only the details panel scrolls:
  // scrollIntoView would also scroll the drawer Paper (clipping the header) and the page behind it.
  useEffect(() => {
    if (!open || !scrollToSpecsOnOpen || loading || !isProductDetailsVisible) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      const container = detailsScrollRef.current;
      const specs = specsRef.current;
      if (!container || !specs) return;
      container.scrollTop +=
        specs.getBoundingClientRect().top - container.getBoundingClientRect().top;
    });
    return () => cancelAnimationFrame(frame);
  }, [open, scrollToSpecsOnOpen, loading, isProductDetailsVisible, productKey]);

  const changeTab = (next: ProductDrawerTab) => {
    setTab(next);
    onTabChange?.(next);
  };

  const telemetryNameSection =
    telemetryNameSectionBySection[telemetrySection] ?? telemetrySection;

  const tp = (suffix: string, extra: { label: string; id?: string }) =>
    telemetryProps({
      name: `${telemetryNameSection}_${suffix}`,
      section: telemetrySection,
      label: extra.label,
      id: extra.id,
    });

  const handleCopyPartNumber = (field: "mfr" | "shi", value: string) => {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedPartNumber(field);
        if (copyResetRef.current) clearTimeout(copyResetRef.current);
        copyResetRef.current = setTimeout(
          () => setCopiedPartNumber(null),
          2000,
        );
      })
      .catch(() => undefined);
  };

  const copyOnKeyDown =
    (field: "mfr" | "shi", value: string) =>
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleCopyPartNumber(field, value);
      }
    };

  const productName = product?.productName ?? "";
  const mfrPartNumber = product?.mfrPartNum ?? "";
  const shiPartNumber = product?.shiPartNumber ?? product?.productDescriptor ?? "";

  const price = product?.price ?? null;
  const formattedPrice = getVisiblePriceLabel(price, formatPrice);
  const accessiblePriceLabel = getAccessiblePriceLabel(price, formatPrice);

  const status = mapInventoryStatusToStockStatus(product?.inventoryStatus);

  const productDescriptor = product?.productDescriptor ?? null;
  const isConfigSystem =
    isConfigSystemProp ??
    product?.productClass?.toLowerCase() === BUNDLE_PRODUCT_CLASS;

  const canAddToCart = hasCart && cart.canAddToCart === true;
  const addToCartDisabledReason =
    hasCart && !canAddToCart ? (cart.disabledReason ?? null) : null;

  const quickDetails = useMemo(() => {
    const visibleDetails = (product?.parsedDescriptionsList ?? []).filter(
      isCustomerVisibleCatalogText,
    );

    if (visibleDetails.length === 0 || !productName.trim()) {
      return visibleDetails;
    }

    const normalizedProductName = normalizeCatalogText(productName);

    return visibleDetails.filter(
      (item) => normalizeCatalogText(item) !== normalizedProductName,
    );
  }, [product?.parsedDescriptionsList, productName]);

  const specSections = useMemo(
    () => fullSpecificationToSections(fullSpecification),
    [fullSpecification],
  );

  const fallbackSpecRows = useMemo<SpecFallbackRow[]>(() => {
    const rows: SpecFallbackRow[] = [];
    const category = product?.category;
    const manufacturer = product?.manufacturer;
    if (category) rows.push({ label: "Category", value: category });
    if (manufacturer) rows.push({ label: "Manufacturer", value: manufacturer });
    if (mfrPartNumber)
      rows.push({ label: "Mfr Part Number", value: mfrPartNumber });
    if (shiPartNumber)
      rows.push({ label: "SHI Part Number", value: shiPartNumber });
    if (specificationFallback?.dimensions) {
      rows.push({ label: "Dimensions", value: specificationFallback.dimensions });
    }
    if (specificationFallback?.weight) {
      rows.push({ label: "Weight", value: specificationFallback.weight });
    }
    return rows;
  }, [
    product?.category,
    product?.manufacturer,
    mfrPartNumber,
    shiPartNumber,
    specificationFallback?.dimensions,
    specificationFallback?.weight,
  ]);

  const productDetails = (
    <Box
      role="tabpanel"
      id="product-drawer-panel-product-details"
      aria-labelledby="product-drawer-tab-product-details"
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {loading ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            p: 4,
          }}
        >
          <CircularProgress aria-label="Loading product details" />
        </Box>
      ) : (
        <Box
          ref={detailsScrollRef}
          data-testid="product-details-scroll"
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: "100%",
            height: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <ProductImageGallery images={images} />

          {(isConfigSystem || hasCart) && (
            <Box sx={{ px: 2.5, pb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isConfigSystem ? (
                  <Button
                    component="a"
                    href={productDetailUrl ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    color="secondary"
                    size="medium"
                    disabled={!productDetailUrl}
                    {...tp("configure", {
                      label: "Configure",
                      id: productDescriptor ?? undefined,
                    })}
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    CONFIGURE
                  </Button>
                ) : (
                  hasCart && (
                    <>
                      <QuantitySelector
                        value={quantity}
                        disabled={!canAddToCart}
                        onDecrease={() => {
                          if (!canAddToCart) return;
                          setQuantity((q) => Math.max(1, q - 1));
                        }}
                        onIncrease={() => {
                          if (!canAddToCart) return;
                          setQuantity((q) => q + 1);
                        }}
                        onValueChange={setQuantity}
                      />
                      <Tooltip
                        title={addToCartDisabledReason ?? ""}
                        placement="top"
                        disableHoverListener={!addToCartDisabledReason}
                        disableFocusListener={!addToCartDisabledReason}
                        disableTouchListener={!addToCartDisabledReason}
                      >
                        {/* Disabled buttons don't fire pointer events, so the span carries the tooltip. */}
                        <Box
                          component="span"
                          tabIndex={addToCartDisabledReason ? 0 : undefined}
                          sx={{ display: "flex", flex: 1, minWidth: 0 }}
                        >
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<ShoppingCartIcon />}
                            size="medium"
                            aria-label="Add to cart"
                            disabled={!canAddToCart}
                            onClick={() => cart.onAddToCart(quantity)}
                            {...tp("add_to_cart", {
                              label: "Add to Cart",
                              id: productDescriptor ?? undefined,
                            })}
                            sx={{
                              fontWeight: 700,
                              letterSpacing: 0.5,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            ADD TO CART
                          </Button>
                        </Box>
                      </Tooltip>
                    </>
                  )
                )}
              </Box>
            </Box>
          )}

          {quickDetails.length > 0 && (
            <Box sx={{ px: 2.5, pb: 2 }}>
              <Typography
                variant="subtitle2"
                fontWeight={400}
                sx={{ mb: 1.25, fontSize: 16 }}
              >
                Quick Details
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {quickDetails.map((item, index) => (
                  <Typography
                    key={`${item}-${index}`}
                    component="li"
                    variant="body2"
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Box ref={specsRef} data-testid="product-specs-anchor">
            <ProductSpecs sections={specSections} fallbackRows={fallbackSpecRows} />
          </Box>
        </Box>
      )}
    </Box>
  );

  const body = (
    <>
      {product && (
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              pt: 1.5,
              bgcolor: "background.paper",
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <CameraOutlinedIcon
                  sx={{ fontSize: 15, color: "text.secondary" }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    fontSize: 11,
                  }}
                >
                  {headerLabel}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={onClose}
                aria-label={`Close ${headerLabel.toLowerCase()}`}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                pb: 1.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                {productDetailUrl ? (
                  <Tooltip title="View Full Details" placement="top">
                    <Link
                      href={productDetailUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      {...tp("view_pdp", {
                        label: "View Full Details",
                        id: productDescriptor ?? undefined,
                      })}
                      sx={{
                        color: "primary.main",
                        fontWeight: 500,
                        fontSize: 17,
                        lineHeight: 1.35,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {productName}
                    </Link>
                  </Tooltip>
                ) : (
                  <Typography
                    variant="subtitle1"
                    fontWeight={500}
                    sx={{ fontSize: 17, lineHeight: 1.35 }}
                  >
                    {productName}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mt: 0.25,
                    flexWrap: "wrap",
                  }}
                >
                  {mfrPartNumber && (
                    <Tooltip
                      title={
                        copiedPartNumber === "mfr"
                          ? "Copied!"
                          : "Copy mfr part #"
                      }
                      placement="top"
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        role="button"
                        tabIndex={0}
                        aria-label={`Copy manufacturer part number ${mfrPartNumber}`}
                        onClick={() =>
                          handleCopyPartNumber("mfr", mfrPartNumber)
                        }
                        onKeyDown={copyOnKeyDown("mfr", mfrPartNumber)}
                        {...tp("copy_mfr_part", {
                          label: "Copy Mfr Part Number",
                          id: mfrPartNumber || undefined,
                        })}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        {mfrPartNumber}
                      </Typography>
                    </Tooltip>
                  )}
                  {mfrPartNumber && shiPartNumber && (
                    <Typography variant="caption" color="text.disabled">
                      &middot;
                    </Typography>
                  )}
                  {shiPartNumber && (
                    <Tooltip
                      title={
                        copiedPartNumber === "shi"
                          ? "Copied!"
                          : "Copy SHI part #"
                      }
                      placement="top"
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        role="button"
                        tabIndex={0}
                        aria-label={`Copy SHI part number ${shiPartNumber}`}
                        onClick={() =>
                          handleCopyPartNumber("shi", shiPartNumber)
                        }
                        onKeyDown={copyOnKeyDown("shi", shiPartNumber)}
                        {...tp("copy_shi_part", {
                          label: "Copy SHI Part Number",
                          id: shiPartNumber || undefined,
                        })}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        {shiPartNumber}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                {hasStockTab ? (
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label={`View stock and inventory. Stock status: ${STOCK_STATUS_CONFIG[status].label}`}
                    onClick={() => changeTab(ProductDrawerTab.StockInventory)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        changeTab(ProductDrawerTab.StockInventory);
                      }
                    }}
                    {...tp("stock_header", {
                      label: "Open Stock Tab (Header)",
                      id: productDescriptor ?? undefined,
                    })}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      justifyContent: "flex-end",
                      cursor: "pointer",
                    }}
                  >
                    <StockDot
                      status={status}
                      label={STOCK_STATUS_CONFIG[status].label}
                      size={7}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      justifyContent: "flex-end",
                    }}
                  >
                    <StockDot
                      status={status}
                      label={STOCK_STATUS_CONFIG[status].label}
                      size={7}
                    />
                  </Box>
                )}
                <Typography
                  variant="subtitle1"
                  sx={{
                    lineHeight: 1.2,
                    mt: 0.25,
                    color: "warning.dark",
                    fontWeight: 400,
                    fontSize: 14,
                  }}
                >
                  <Box component="span" sx={srOnlySx}>
                    {accessiblePriceLabel}
                  </Box>
                  <Box component="span" aria-hidden="true">
                    {formattedPrice}
                  </Box>
                </Typography>
              </Box>
            </Box>

            {hasStockTab && (
              <Box
                sx={{ mx: -2.5, borderTop: "1px solid", borderColor: "divider" }}
              >
                <Tabs
                  value={tab}
                  onChange={(_, value) => changeTab(value as ProductDrawerTab)}
                  indicatorColor="secondary"
                  textColor="inherit"
                  variant="fullWidth"
                  sx={{ minHeight: 40 }}
                >
                  <Tab
                    label="PRODUCT DETAILS"
                    id="product-drawer-tab-product-details"
                    aria-controls="product-drawer-panel-product-details"
                    {...tp("tab_product_details", {
                      label: "Product Details Tab",
                    })}
                    sx={{ fontSize: 14, fontWeight: 500, minHeight: 40 }}
                  />
                  <Tab
                    label="STOCK / INVENTORY"
                    id="product-drawer-tab-stock-inventory"
                    aria-controls="product-drawer-panel-stock-inventory"
                    {...tp("tab_stock_inventory", {
                      label: "Stock / Inventory Tab",
                    })}
                    sx={{ fontSize: 14, fontWeight: 500, minHeight: 40 }}
                  />
                </Tabs>
              </Box>
            )}
          </Box>

          {/* Body */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            {isProductDetailsVisible && productDetails}

            {hasStockTab && tab === ProductDrawerTab.StockInventory && (
              <Box
                role="tabpanel"
                id="product-drawer-panel-stock-inventory"
                aria-labelledby="product-drawer-tab-stock-inventory"
                sx={{
                  display: "flex",
                  flex: 1,
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <ProductStockInventoryTab
                  data={stockTab.data}
                  isLoading={stockTab.isLoading}
                  isError={stockTab.isError}
                  onRefresh={stockTab.onRefresh}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
      {!product && (
        <Box sx={{ p: 3 }}>
          <Typography color="error">
            Failed to load product details
          </Typography>
        </Box>
      )}
    </>
  );

  if (renderShell) {
    return <>{renderShell(body)}</>;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      role="dialog"
      aria-label={`${headerLabel} - ${productName}`}
      sx={zIndex != null ? { zIndex } : undefined}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", sm: 580 },
            maxWidth: "100vw",
            boxSizing: "border-box",
            overflowX: "hidden",
            overflowY: "hidden",
            backgroundColor: "background.paper",
          },
        },
      }}
    >
      {body}
    </Drawer>
  );
}
